
import React, { RefObject } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import LegalSection from './LegalSection';
import { Link } from 'react-router-dom';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

interface LegalServicesProps {
  servicesRef: RefObject<HTMLHeadingElement>;
}

const LegalServices: React.FC<LegalServicesProps> = ({ servicesRef }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const { 
    getServicePath, 
    getIndustryPath,
    getComparisonPath
  } = useNavigationUtils();

  // Service link text based on language
  const serviceLabels = {
    productVideos: {
      de: 'KI-generierte Produktvideos',
      en: 'AI-generated product videos',
      fr: 'Vidéos de produits générées par IA',
      it: 'Video di prodotti generati da IA'
    },
    socialMediaAds: {
      de: 'KI-Social-Media-Werbung',
      en: 'AI social media advertising',
      fr: 'Publicités sur médias sociaux IA',
      it: 'Pubblicità social media IA'
    },
    comparison: {
      de: 'KI-Videomarketing vs. traditionelle Produktion',
      en: 'AI video marketing vs. traditional production',
      fr: 'Marketing vidéo IA vs. production traditionnelle',
      it: 'Marketing video IA vs. produzione tradizionale'
    },
    financeIndustry: {
      de: 'KI-Videomarketing für Finanzdienstleister',
      en: 'AI video marketing for financial services',
      fr: 'Marketing vidéo IA pour services financiers',
      it: 'Marketing video IA per servizi finanziari'
    },
    luxuryIndustry: {
      de: 'KI-Videomarketing für Luxusmarken',
      en: 'AI video marketing for luxury brands',
      fr: 'Marketing vidéo IA pour marques de luxe',
      it: 'Marketing video IA per marchi di lusso'
    },
    tourismIndustry: {
      de: 'KI-Videomarketing für Tourismus',
      en: 'AI video marketing for tourism',
      fr: 'Marketing vidéo IA pour tourisme',
      it: 'Marketing video IA per turismo'
    },
    healthcareIndustry: {
      de: 'KI-Videomarketing für Gesundheitswesen',
      en: 'AI video marketing for healthcare',
      fr: 'Marketing vidéo IA pour santé',
      it: 'Marketing video IA per sanità'
    }
  };

  return (
    <LegalSection id="our-services" 
      title={currentLanguage === 'de' ? 'Unsere Leistungen' : 
             currentLanguage === 'en' ? 'Our Services' : 
             currentLanguage === 'fr' ? 'Nos Services' : 'I Nostri Servizi'} 
      titleRef={servicesRef}>
      <p className="mb-6 text-gray-300">
        {currentLanguage === 'de' ? 
          'Future Level bietet innovative KI-Videomarketing-Lösungen für Schweizer Unternehmen:' : 
          currentLanguage === 'en' ? 
          'Future Level offers innovative AI video marketing solutions for Swiss businesses:' :
          currentLanguage === 'fr' ? 
          'Future Level propose des solutions innovantes de marketing vidéo IA pour les entreprises suisses:' :
          'Future Level offre soluzioni innovative di marketing video IA per aziende svizzere:'}
      </p>
      
      <div className="mb-6">
        <h4 className="text-xl text-purple-200 mb-3">
          {currentLanguage === 'de' ? 'Unsere Kernleistungen' : 
           currentLanguage === 'en' ? 'Our Core Services' :
           currentLanguage === 'fr' ? 'Nos Services Principaux' :
           'I Nostri Servizi Principali'}
        </h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <Link to={getServicePath('product-videos')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.productVideos[currentLanguage as keyof typeof serviceLabels.productVideos]}
            </Link>
          </li>
          <li>
            <Link to={getServicePath('social-media-ads')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.socialMediaAds[currentLanguage as keyof typeof serviceLabels.socialMediaAds]}
            </Link>
          </li>
          <li>
            <Link to={getComparisonPath()} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.comparison[currentLanguage as keyof typeof serviceLabels.comparison]}
            </Link>
          </li>
        </ul>
      </div>

      <div className="mb-6">
        <h4 className="text-xl text-purple-200 mb-3">
          {currentLanguage === 'de' ? 'Branchenspezifische Lösungen' : 
           currentLanguage === 'en' ? 'Industry-Specific Solutions' :
           currentLanguage === 'fr' ? 'Solutions Spécifiques à l\'Industrie' :
           'Soluzioni Specifiche per Settore'}
        </h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <Link to={getIndustryPath('finance')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.financeIndustry[currentLanguage as keyof typeof serviceLabels.financeIndustry]}
            </Link>
          </li>
          <li>
            <Link to={getIndustryPath('luxury')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.luxuryIndustry[currentLanguage as keyof typeof serviceLabels.luxuryIndustry]}
            </Link>
          </li>
          <li>
            <Link to={getIndustryPath('tourism')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.tourismIndustry[currentLanguage as keyof typeof serviceLabels.tourismIndustry]}
            </Link>
          </li>
          <li>
            <Link to={getIndustryPath('healthcare')} className="text-purple-400 hover:text-purple-300 underline">
              {serviceLabels.healthcareIndustry[currentLanguage as keyof typeof serviceLabels.healthcareIndustry]}
            </Link>
          </li>
        </ul>
      </div>
    </LegalSection>
  );
};

export default LegalServices;
